import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Tags, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { ConfirmDialog, type ConfirmState } from '../../components/ui/confirm';
import { ActionMenu } from '../../components/ui/action-menu';
import { PaginationControls, useControlledPagination } from '../../components/ui/pagination';
import { SearchBox } from '../../components/ui/search';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/state';
import { Table, Td, Th } from '../../components/ui/table';
import { api } from '../../shared/api';
import { latestUsablePrice } from '../../shared/billing';
import { formatCurrency } from '../../shared/format';
import { copy, labelForServiceCategory } from '../../shared/i18n';
import { useUrlListFilters } from '../../shared/listFilters';
import { matchesSearch } from '../../shared/search';
import type { Service } from '../../../../../packages/contracts/src/index.js';

const serviceCategories: Service['category'][] = ['daycare', 'boarding', 'grooming', 'spa', 'transportation', 'medical', 'treats', 'fee', 'other'];

export function ServicesPage() {
  const queryClient = useQueryClient();
  const defaults = useMemo(() => ({ q: '', status: '', category: '', page: '1', pageSize: '10' }), []);
  const { values: filters, setValue } = useUrlListFilters(defaults);
  const search = filters.q;
  const statusFilter = filters.status;
  const categoryFilter = filters.category;
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const page = Math.max(1, Number(filters.page) || 1);
  const pageSize = Math.max(10, Number(filters.pageSize) || 10);
  const serviceListOptions = { limit: page * pageSize + 1, q: search, status: statusFilter, category: categoryFilter };
  const servicesQuery = useQuery({ queryKey: ['services', serviceListOptions], queryFn: () => api.services.list(serviceListOptions) });
  const services = servicesQuery.data?.items ?? [];
  const filteredServices = useMemo(
    () => services.filter((service) => {
      if (statusFilter === 'active' && service.active === false) return false;
      if (statusFilter === 'inactive' && service.active !== false) return false;
      if (categoryFilter && service.category !== categoryFilter) return false;
      return matchesSearch(search, [service.name, service.description, labelForServiceCategory(service.category), service.active ? copy.common.active : copy.common.inactive]);
    }),
    [categoryFilter, search, services, statusFilter],
  );
  const servicesPage = useControlledPagination(filteredServices, page, (nextPage) => setValue('page', String(nextPage)), pageSize);
  const serviceIds = useMemo(() => servicesPage.pageItems.map((service) => service.id).sort(), [servicesPage.pageItems]);
  const currentPricesQuery = useQuery({
    queryKey: ['services', 'current-prices', serviceIds.join('|')],
    queryFn: async () => {
      const currentDate = new Date().toISOString().slice(0, 10);
      const pricesByServiceId: Record<string, number> = {};
      await Promise.all(serviceIds.map(async (serviceId) => {
        const prices = await api.services.prices(serviceId, { limit: 100 });
        const currentPrice = latestUsablePrice(prices.items, currentDate);
        if (currentPrice) pricesByServiceId[serviceId] = currentPrice.amountCents;
      }));
      return pricesByServiceId;
    },
    enabled: serviceIds.length > 0,
  });

  const deleteService = useMutation({
    mutationFn: api.services.remove,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['services'] });
      await queryClient.invalidateQueries({ queryKey: ['stays'] });
      await queryClient.invalidateQueries({ queryKey: ['daycares'] });
    },
  });

  const confirmDelete = (serviceId: string, serviceName: string) => setConfirm({
    title: 'Eliminar servicio',
    body: copy.services.deleteConfirm(serviceName),
    confirmLabel: 'Eliminar',
    onConfirm: () => deleteService.mutate(serviceId),
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-col items-start gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-950">{copy.services.title}</h1>
          <p className="mt-1 text-sm text-slate-500">{copy.services.body}</p>
        </div>
        <Link className="inline-flex h-9 items-center gap-2 rounded-md border border-[#d6ded0] bg-white px-3 text-sm font-medium text-[#334033] hover:bg-[#eef3ea]" to="/admin/services/new">
          Nuevo servicio
        </Link>
      </div>

      <section>
        <Card>
          <CardHeader>
            <div className="grid gap-3">
              <div className="flex flex-col items-start gap-3">
                <h2 className="text-sm font-semibold">{copy.services.catalog}</h2>
                <SearchBox value={search} onChange={(value) => setValue('q', value)} placeholder="Buscar servicio" label="Buscar servicio" />
              </div>
              <div className="grid min-w-0 gap-2 md:grid-cols-2">
                <SegmentedControl
                  value={statusFilter}
                  options={[{ id: '', label: 'Todos' }, { id: 'active', label: 'Activos' }, { id: 'inactive', label: 'Inactivos' }]}
                  onChange={(nextStatus) => setValue('status', nextStatus)}
                  ariaLabel="Filtrar servicios por estado"
                />
                <SegmentedControl
                  value={categoryFilter}
                  options={[{ id: '', label: 'Todas las categorias' }, ...serviceCategories.map((category) => ({ id: category, label: labelForServiceCategory(category) }))]}
                  onChange={(nextCategory) => setValue('category', nextCategory)}
                  ariaLabel="Filtrar servicios por categoria"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {servicesQuery.isLoading ? <LoadingState label={copy.services.loading} /> : null}
            {servicesQuery.isError ? <ErrorState message={servicesQuery.error.message} /> : null}
            {servicesQuery.data && servicesQuery.data.items.length === 0 ? <EmptyState title={copy.services.emptyTitle} body={copy.services.emptyBody} /> : null}
            {servicesQuery.data && servicesQuery.data.items.length > 0 && filteredServices.length === 0 ? <EmptyState title="Sin resultados" body="Intenta con otro nombre, categoría o estado." /> : null}
            {servicesQuery.data && filteredServices.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <thead>
                    <tr>
                      <Th>{copy.common.name}</Th>
                      <Th>{copy.common.category}</Th>
                      <Th>Precio actual</Th>
                      <Th>{copy.common.status}</Th>
                      <Th>{copy.common.description}</Th>
                      <Th>{copy.common.actions}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {servicesPage.pageItems.map((service) => (
                      <tr key={service.id}>
                        <Td className="font-medium text-slate-900">{service.name}</Td>
                        <Td>{labelForServiceCategory(service.category)}</Td>
                        <Td>{typeof currentPricesQuery.data?.[service.id] === 'number' ? formatCurrency(currentPricesQuery.data[service.id]) : '-'}</Td>
                        <Td><Badge className={service.active ? 'bg-[#eef3ea] text-[#596a52]' : 'bg-[#f3e5df] text-[#7d4f43]'}>{service.active ? copy.common.active : copy.common.inactive}</Badge></Td>
                        <Td className="max-w-96 truncate">{service.description ?? copy.services.noDescription}</Td>
                        <Td>
                          <ActionMenu
                            groups={[
                              {
                                label: 'Gestion',
                                children: (
                                  <>
                                    <Link className="flex items-center gap-2 rounded-md px-2 py-2 text-[#334033] hover:bg-[#eef3ea]" to={`/admin/services/${service.id}/edit`}>
                                      <Pencil className="h-4 w-4" aria-hidden="true" />
                                      Editar
                                    </Link>
                                    <Link className="flex items-center gap-2 rounded-md px-2 py-2 text-[#334033] hover:bg-[#eef3ea]" to={`/admin/prices?serviceId=${encodeURIComponent(service.id)}`}>
                                      <Tags className="h-4 w-4" aria-hidden="true" />
                                      Ver precios
                                    </Link>
                                  </>
                                ),
                              },
                              {
                                label: 'Riesgo',
                                tone: 'danger',
                                children: (
                                  <button
                                    type="button"
                                    className="flex items-center gap-2 rounded-md px-2 py-2 text-left text-[#8a4a3a] hover:bg-[#fff8f8] disabled:cursor-not-allowed disabled:opacity-60"
                                    aria-label={copy.services.deleteLabel(service.name)}
                                    disabled={deleteService.isPending}
                                    onClick={() => confirmDelete(service.id, service.name)}
                                  >
                                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                                    Eliminar
                                  </button>
                                ),
                              },
                            ]}
                          />
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : null}
            <PaginationControls {...servicesPage} onPageChange={servicesPage.setPage} onPageSizeChange={(nextPageSize) => setValue('pageSize', String(nextPageSize))} />
            {deleteService.isError ? <p className="mt-3 text-sm text-rose-600">{deleteService.error.message}</p> : null}
          </CardContent>
        </Card>
      </section>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
